import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
} from '@mui/material';

interface NewCashExpenseDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { title: string; amount: number; taxDeductible: number; date: string }) => void;
  loading?: boolean;
}

interface CashExpenseFormValues {
  title: string;
  amount: string;
  taxDeductible: string;
  date: string;
}

export function NewCashExpenseDialog({ open, onClose, onSubmit, loading = false }: NewCashExpenseDialogProps) {
  const taxDeductibleEdited = useRef(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CashExpenseFormValues>({
    defaultValues: {
      title: '',
      amount: '',
      taxDeductible: '',
      date: new Date().toISOString().substring(0, 10),
    },
  });

  useEffect(() => {
    if (open) {
      taxDeductibleEdited.current = false;
      reset({
        title: '',
        amount: '',
        taxDeductible: '',
        date: new Date().toISOString().substring(0, 10),
      });
    }
  }, [open, reset]);

  const amountValue = watch('amount');

  useEffect(() => {
    if (!taxDeductibleEdited.current) {
      setValue('taxDeductible', amountValue);
    }
  }, [amountValue, setValue]);

  const onFormSubmit = (values: CashExpenseFormValues) => {
    const parsedAmount = parseFloat(values.amount);
    const parsedTaxDeductible = parseFloat(values.taxDeductible);
    const effectiveTaxDeductible = !isNaN(parsedTaxDeductible) ? parsedTaxDeductible : parsedAmount;

    onSubmit({
      title: values.title.trim(),
      amount: parsedAmount,
      taxDeductible: effectiveTaxDeductible,
      date: new Date(values.date).toISOString(),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <DialogTitle>Hotovostný výdavok</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Názov *"
                {...register('title', { validate: (v) => !!v.trim() || 'Názov je povinný' })}
                error={!!errors.title}
                helperText={errors.title?.message ?? ''}
                fullWidth
                variant="standard"
                disabled={loading}
                autoFocus
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Suma (€) *"
                type="number"
                {...register('amount', {
                  required: 'Zadajte platnú sumu',
                  validate: (v) => parseFloat(v) > 0 || 'Zadajte platnú sumu',
                })}
                error={!!errors.amount}
                helperText={errors.amount?.message ?? ''}
                fullWidth
                variant="standard"
                disabled={loading}
                inputProps={{ step: '0.01', min: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Odpočítateľné z dane (€)"
                type="number"
                {...register('taxDeductible', {
                  onChange: () => {
                    taxDeductibleEdited.current = true;
                  },
                })}
                fullWidth
                variant="standard"
                disabled={loading}
                inputProps={{ step: '0.01', min: '0' }}
                helperText="Predvolene = celá suma"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Dátum výdavku *"
                type="date"
                {...register('date', { required: true })}
                fullWidth
                variant="standard"
                disabled={loading}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Zrušiť
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            Vytvoriť
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
